import React, { useEffect, useRef, useMemo } from 'react';
import Image from 'utils/image';
import Layout from 'utils/layout';
import Video from 'utils/video';
import useUniqueId from 'utils/unique-id';
import PlayIcon from 'utils/icon/icon-play.svg';
import PauseIcon from 'utils/icon/icon-pause.svg';
import clsx from 'clsx';

const VideoMedia = (media) => {
  const uniquePrefix = useUniqueId();
  const layout = Layout.getInstance();
  const videoPlayer = useRef(null);
  const video = useRef(null);
  const videoContainer = useRef(null);
  const config = JSON.stringify({
    autoplay: true,
    controls: true,
    nativeControls: false,
    videoDesktop:
      media.host === 'VIMEO'
        ? media.external_id
        : {
            id: media.id,
          },
    host: media.host?.toLowerCase() || 'shopify',
    play: {
      selector: '',
    },
    mute: {
      selector: '',
    },
    uniquePrefix,
  });
  const videoUrl = useMemo(() => {
    const params = {
      title: 0,
      muted: 1,
      controls: 0,
      autoplay: media.autoplay === true ? 1 : 0,
      autopause: 0,
      loop: 1,
    };
    return `https://player.vimeo.com/video/${media.external_id}?${new URLSearchParams(params)}`;
  }, [media]);
  const ratio = (media.previewImage.height / media.previewImage.width) * 100;

  useEffect(() => {
    if (video.current && videoContainer.current) {
      videoPlayer.current = new Video({ element: videoContainer.current, video: video.current, layout });
    }
  }, []);

  return (
    <div ref={videoContainer}>
      <div
        className="relative w-full"
        ref={video}
        data-component="video"
      >
        <script
          type="application/json"
          data-component-data
          dangerouslySetInnerHTML={{ __html: config }}
        ></script>
        {media.host === 'VIMEO' ? (
          <div
            className="relative w-full"
            style={{ paddingTop: `${ratio}%` }}
          >
            <iframe
              id={`video-${uniquePrefix}-${media.external_id}`}
              src={videoUrl}
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              className="absolute left-0 top-0 h-full w-full"
              data-dom-player
            ></iframe>
          </div>
        ) : (
          <div id={`video-${media.id}`}>
            <video
              className="w-full"
              autoPlay={media.autoplay}
              muted="true"
              loop="true"
              playsInline
            >
              {media.sources.map((source) => (
                <source
                  key={source.format}
                  src={source.url}
                  type={`video/${source.format}`}
                ></source>
              ))}
            </video>
          </div>
        )}
        <div
          className="absolute inset-0 z-10 flex cursor-pointer  items-end px-3.5 py-4.5 md:justify-end"
          data-video-play-toggle
        >
          <div
            className="flex"
            data-video-controls
          >
            <button
              data-video-play
              type="button"
              aria-label=""
              className="inline-flex h-7.5 w-7.5 items-center justify-center rounded-full border border-black bg-white transition-colors duration-200 ease-in-out hover:border-brand-tangerine"
            >
              <span className={clsx('icon-play', { hidden: media.autoplay })}>
                <PlayIcon className="relative left-px h-auto w-3" />
              </span>
              <span className={clsx('icon-pause', { hidden: !media.autoplay })}>
                <PauseIcon className="h-auto w-2" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Media = ({ mediaContentType, previewImage, srcWidths, classes, autoplay = true, ...media }) => {
  if (mediaContentType === 'EXTERNAL_VIDEO' || mediaContentType === 'VIDEO') {
    return (
      <VideoMedia
        autoplay={autoplay}
        {...media}
        previewImage={previewImage}
      />
    );
  }

  return (
    <Image
      srcWidths={srcWidths}
      classes={classes}
      {...previewImage}
    />
  );
};

export default Media;
